import { useState, useRef, useEffect, useCallback } from 'react';
import { fetchByISBN, addBDsBatch } from '../utils/firestoreService';
import { emptyBD } from '../utils/bdDefaults';
import { useAuth } from '../hooks/useAuth';

const F = "'Inter',-apple-system,sans-serif";
const D = "'Bangers','Arial Black',cursive";
const T = {
  orange:'var(--orange)', ink:'var(--ink)', surface:'var(--surface)', bg:'var(--bg)',
  border:'var(--border)', muted:'var(--muted)', subtle:'var(--subtle)',
  g1:'var(--surface2)', green:'#16A34A', red:'#DC2626',
};

let quaggaPromise = null;
const loadQuagga = () => {
  if (quaggaPromise) return quaggaPromise;
  quaggaPromise = new Promise((resolve, reject) => {
    if (window.Quagga) { resolve(window.Quagga); return; }
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/quagga/0.12.1/quagga.min.js';
    s.onload = () => resolve(window.Quagga);
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return quaggaPromise;
};

const COOLDOWN_MS = 2200; // évite de re-scanner le même code juste après

export default function RafaleScanModal({ onClose, onToast }) {
  const { user } = useAuth();
  const videoRef   = useRef(null);
  const streamRef  = useRef(null);
  const rafRef     = useRef(null);
  const quaggaRef  = useRef(null);
  const mountedRef = useRef(true);
  const lastRef    = useRef({ code: null, at: 0 });
  const busyRef    = useRef(false); // un lookup API est en cours : ignorer les détections

  const [phase, setPhase]   = useState('camera'); // camera | review
  const [starting, setStart]= useState(true);
  const [camErr, setCamErr] = useState('');
  const [flash, setFlash]   = useState(null); // dernier item ajouté, pour le petit feedback visuel
  const [queue, setQueue]   = useState([]);   // [{tempId, serie, numero, titre, isbn, found}]
  const [saving, setSaving] = useState(false);

  const stopAll = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (quaggaRef.current) { try { quaggaRef.current.stop(); } catch {} quaggaRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; stopAll(); };
  }, [stopAll]);

  const handleCode = useCallback(async (code) => {
    const clean = code.replace(/[\s-]/g, '');
    const now = Date.now();
    if (lastRef.current.code === clean && now - lastRef.current.at < COOLDOWN_MS) return; // doublon immédiat
    if (busyRef.current) return;
    lastRef.current = { code: clean, at: now };

    const isISBN = /^(978|979)\d{10}$/.test(clean);
    const isEAN  = /^\d{13}$/.test(clean);
    const isISBN10 = /^\d{10}$/.test(clean);
    if (!isISBN && !isEAN && !isISBN10) {
      setFlash({ ok:false, label:`Code non reconnu (${clean})` });
      return;
    }

    busyRef.current = true;
    const result = await fetchByISBN(clean).catch(()=>null);
    busyRef.current = false;
    if (!mountedRef.current) return;

    const item = {
      tempId: `${clean}-${now}`,
      isbn: clean,
      serie: result?.serie || '',
      numero: result?.numero || '',
      titre: result?.titre || '',
      auteur: result?.auteur || '',
      scenariste: result?.scenariste || '',
      dessinateur: result?.dessinateur || '',
      editeur: result?.editeur || '',
      genre: result?.genre || '',
      langue: result?.langue || 'Français',
      found: !!result,
    };
    setQueue(q => [...q, item]);
    setFlash({
      ok: !!result,
      label: result
        ? (result.serie ? `${result.serie}${result.numero?` T.${result.numero}`:''}` : (result.titre || 'Album ajouté'))
        : `ISBN ${clean} — infos non trouvées`,
    });
  }, []);

  const startWithBarcodeDetector = useCallback(() => {
    const bd = new window.BarcodeDetector({ formats: ['ean_13','ean_8','upc_a','upc_e','code_128'] });
    const video = videoRef.current;
    const scan = async () => {
      if (!mountedRef.current || !streamRef.current) return;
      try {
        const codes = await bd.detect(video);
        if (codes.length > 0) handleCode(codes[0].rawValue);
      } catch {}
      rafRef.current = requestAnimationFrame(scan);
    };
    rafRef.current = requestAnimationFrame(scan);
  }, [handleCode]);

  const startWithQuagga = useCallback(async () => {
    const Quagga = await loadQuagga();
    quaggaRef.current = Quagga;
    if (!mountedRef.current) return;
    const video = videoRef.current;
    await new Promise((resolve, reject) => {
      Quagga.init({
        inputStream: { type:'LiveStream', target: video, constraints: { facingMode:'environment', width:{ideal:1280}, height:{ideal:720} } },
        decoder: { readers: ['ean_reader','ean_8_reader','upc_reader','upc_e_reader','code_128_reader'] },
        locate: true, numOfWorkers: 0,
      }, err => err ? reject(err) : resolve());
    });
    Quagga.start();
    Quagga.onDetected(result => {
      const code = result.codeResult?.code;
      if (code && mountedRef.current) handleCode(code);
    });
  }, [handleCode]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setStart(true); setCamErr('');
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode:{ideal:'environment'}, width:{ideal:1280}, height:{ideal:720} }, audio:false,
        });
        if (cancelled) { stream.getTracks().forEach(t=>t.stop()); return; }
        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) return;
        video.playsInline = true; video.muted = true; video.autoplay = true;
        video.setAttribute('playsinline',''); video.setAttribute('webkit-playsinline','');
        video.srcObject = stream;
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(()=>reject(new Error('Timeout démarrage vidéo')), 10000);
          const onPlay = () => { clearTimeout(timeout); video.removeEventListener('playing', onPlay); resolve(); };
          video.addEventListener('playing', onPlay);
          video.play().catch(reject);
        });
        if (!mountedRef.current || cancelled) return;
        setStart(false);
        if ('BarcodeDetector' in window) startWithBarcodeDetector();
        else await startWithQuagga();
      } catch(e) {
        if (!mountedRef.current || cancelled) return;
        setStart(false); stopAll();
        const msg = e?.message || String(e);
        if (/NotAllowed|Permission|denied/i.test(msg) || e?.name==='NotAllowedError') {
          setCamErr("Accès à la caméra refusé.\n\n📱 iPhone : Réglages → Safari → Caméra → Autoriser\n🤖 Android : Autorisations du navigateur → Caméra");
        } else if (e?.name === 'NotFoundError') setCamErr('Aucune caméra trouvée.');
        else if (e?.name === 'NotReadableError') setCamErr('Caméra occupée par une autre app.');
        else setCamErr(`Erreur : ${msg}`);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(()=>setFlash(null), 1600);
    return () => clearTimeout(t);
  }, [flash]);

  const removeItem = (tempId) => setQueue(q => q.filter(x=>x.tempId!==tempId));
  const editItem = (tempId, patch) => setQueue(q => q.map(x=>x.tempId===tempId?{...x,...patch}:x));

  const finish = () => { stopAll(); setPhase('review'); };

  const handleSave = async () => {
    if (!queue.length) return;
    setSaving(true);
    try {
      const items = queue.map(it => emptyBD({
        serie: it.serie, numero: it.numero, titre: it.titre,
        auteur: it.auteur, scenariste: it.scenariste, dessinateur: it.dessinateur,
        editeur: it.editeur, genre: it.genre, langue: it.langue, isbn: it.isbn,
      }));
      const ids = await addBDsBatch(user.uid, items);
      onToast?.(`${ids.length} album${ids.length>1?'s':''} ajouté${ids.length>1?'s':''} ✓`);
      onClose();
    } catch(e) { onToast?.('Erreur : '+e.message, 'error'); setSaving(false); }
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(28,22,18,.85)',zIndex:1100,display:'flex',flexDirection:'column'}}>
      {/* Header */}
      <div style={{padding:'1rem 1.25rem',display:'flex',alignItems:'center',justifyContent:'space-between',background:'rgba(0,0,0,.35)',flexShrink:0}}>
        <div>
          <div style={{fontFamily:D,fontSize:'1.2rem',letterSpacing:'.05em',color:'#fff'}}>📷 Scan en rafale</div>
          <div style={{fontSize:'12px',color:'rgba(255,255,255,.7)',fontFamily:F}}>
            {phase==='camera' ? `${queue.length} album${queue.length>1?'s':''} scanné${queue.length>1?'s':''}` : `Vérifie avant d'ajouter`}
          </div>
        </div>
        <button onClick={phase==='camera' ? (queue.length?finish:onClose) : onClose}
          style={{width:'34px',height:'34px',borderRadius:'50%',border:'1px solid rgba(255,255,255,.3)',background:'rgba(255,255,255,.1)',color:'#fff',fontSize:'17px',cursor:'pointer'}}>×</button>
      </div>

      {phase==='camera' ? (
        <>
          <div style={{flex:1,position:'relative',minHeight:0,background:'#000'}}>
            <video ref={videoRef} playsInline muted autoPlay style={{width:'100%',height:'100%',objectFit:'cover',display:'block',opacity:starting?0:1,transition:'opacity .3s'}}/>

            {starting && !camErr && (
              <div style={{position:'absolute',inset:0,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:'10px',color:'rgba(255,255,255,.85)'}}>
                <div style={{width:32,height:32,border:'3px solid rgba(240,78,26,.3)',borderTopColor:'var(--orange)',borderRadius:'50%',animation:'rafspin .7s linear infinite'}}/>
                <div style={{fontSize:'13px',fontWeight:600,fontFamily:F}}>Démarrage de la caméra…</div>
              </div>
            )}

            {!starting && !camErr && (
              <>
                <svg style={{position:'absolute',inset:0,width:'100%',height:'100%',pointerEvents:'none'}} viewBox="0 0 100 100" preserveAspectRatio="none">
                  <path fillRule="evenodd" fill="rgba(0,0,0,.35)" d="M0 0h100v100H0z M8 38h84v24H8z"/>
                  <rect x="8" y="38" width="84" height="24" fill="none" stroke="rgba(240,78,26,.85)" strokeWidth="0.6" rx="1"/>
                </svg>
                <div style={{position:'absolute',top:'12px',left:0,right:0,display:'flex',justifyContent:'center'}}>
                  <div style={{background:'rgba(0,0,0,.6)',color:'rgba(255,255,255,.9)',fontSize:'12px',fontWeight:600,padding:'6px 14px',borderRadius:'99px',fontFamily:F}}>
                    🔍 Vise le prochain code-barres…
                  </div>
                </div>
              </>
            )}

            {camErr && (
              <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',padding:'1.5rem'}}>
                <div style={{background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:'12px',padding:'16px',fontSize:'13px',color:T.red,lineHeight:1.7,whiteSpace:'pre-line',maxWidth:'360px'}}>⚠️ {camErr}</div>
              </div>
            )}

            {/* Flash de confirmation */}
            {flash && (
              <div style={{position:'absolute',bottom:'16px',left:'16px',right:'16px',display:'flex',justifyContent:'center'}}>
                <div style={{background: flash.ok ? 'rgba(22,163,74,.95)' : 'rgba(217,119,6,.95)', color:'#fff', padding:'10px 16px', borderRadius:'12px', fontSize:'13px', fontWeight:700, fontFamily:F, boxShadow:'0 6px 20px rgba(0,0,0,.3)', display:'flex', alignItems:'center', gap:'8px', animation:'rafpop .25s ease'}}>
                  <span>{flash.ok?'✓':'⚠️'}</span>{flash.label}
                </div>
              </div>
            )}
          </div>

          <div style={{padding:'1rem 1.25rem',background:'rgba(0,0,0,.35)',flexShrink:0}}>
            <button onClick={finish} disabled={!queue.length}
              style={{width:'100%',padding:'14px',border:'none',borderRadius:'12px',background:queue.length?T.orange:'rgba(255,255,255,.15)',color:'#fff',fontFamily:F,fontSize:'15px',fontWeight:700,cursor:queue.length?'pointer':'default'}}>
              {queue.length ? `Terminer (${queue.length}) →` : 'Scanne ton premier album…'}
            </button>
          </div>
        </>
      ) : (
        <div style={{flex:1,overflowY:'auto',background:T.bg,borderRadius:'18px 18px 0 0',padding:'1.25rem'}}>
          {queue.length===0 ? (
            <div style={{textAlign:'center',padding:'2.5rem 1rem',color:T.muted,fontFamily:F}}>Aucun album scanné.</div>
          ) : (
            <div style={{display:'flex',flexDirection:'column',gap:'8px',marginBottom:'16px'}}>
              {queue.map(it => (
                <div key={it.tempId} style={{display:'flex',alignItems:'center',gap:'8px',background:T.surface,border:`1px solid ${T.border}`,borderRadius:'10px',padding:'8px 10px'}}>
                  <div style={{width:34,height:34,borderRadius:'8px',background:it.found?'#F0FDF4':'#FFFBEB',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'15px',flexShrink:0}}>{it.found?'📕':'⚠️'}</div>
                  <input value={it.serie} onChange={e=>editItem(it.tempId,{serie:e.target.value})} placeholder="Série"
                    style={{flex:2,minWidth:0,padding:'6px 8px',border:`1px solid ${T.border}`,borderRadius:'7px',fontFamily:F,fontSize:'12.5px',background:T.bg,color:T.ink,outline:'none'}}/>
                  <input value={it.numero} onChange={e=>editItem(it.tempId,{numero:e.target.value})} placeholder="N°"
                    style={{width:44,padding:'6px 6px',border:`1px solid ${T.border}`,borderRadius:'7px',fontFamily:F,fontSize:'12.5px',background:T.bg,color:T.ink,outline:'none',textAlign:'center'}}/>
                  <button onClick={()=>removeItem(it.tempId)} style={{width:28,height:28,borderRadius:'7px',border:'none',background:'transparent',color:T.red,fontSize:'13px',cursor:'pointer',flexShrink:0}}>✕</button>
                </div>
              ))}
            </div>
          )}
          <div style={{display:'flex',gap:'8px'}}>
            <button onClick={()=>{setPhase('camera');lastRef.current={code:null,at:0};}} style={{flex:1,padding:'12px',border:`1.5px solid ${T.border}`,borderRadius:'11px',background:T.surface,color:T.ink,fontFamily:F,fontSize:'14px',fontWeight:600,cursor:'pointer'}}>← Reprendre le scan</button>
            <button onClick={handleSave} disabled={saving||!queue.length} style={{flex:2,padding:'12px',border:'none',borderRadius:'11px',background:queue.length?T.orange:T.subtle,color:'#fff',fontFamily:F,fontSize:'14px',fontWeight:700,cursor:queue.length?'pointer':'default'}}>
              {saving?'Ajout en cours…':`Ajouter ${queue.length} album${queue.length>1?'s':''}`}
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes rafspin { to{transform:rotate(360deg)} }
        @keyframes rafpop  { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
    </div>
  );
}
